import React from 'react';
import {useParams} from 'react-router-dom';
import {useProjectGroupByProjectUuidQuery} from 'api';
import {Combobox, Transition} from '@headlessui/react';
import {ChevronUpDownIcon, XMarkIcon} from '@heroicons/react/20/solid';

interface Props {
  userIds: string[];
  onChange: (userIds: string[]) => void;
}

const kanaToHira = (str: string) =>
  str.replace(/[\u30a1-\u30f6]/g, (match) => {
    const chr = match.charCodeAt(0) - 0x60;
    return String.fromCharCode(chr);
  });

const UsersSelect = ({userIds, onChange}: Props) => {
  const {projectId} = useParams<{projectId: string}>();
  const [keyword, setKeyword] = React.useState('');
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const {data: {projectGroupByProjectUuid: {users = []} = {}} = {}} =
    useProjectGroupByProjectUuidQuery({
      variables: {uuid: projectId},
    });

  const filteredUsers = React.useMemo(
    () =>
      users
        ?.filter((user) => !userIds.includes(user.id))
        ?.filter((user) =>
          kanaToHira(`${user.lastName}${user.firstName}`).includes(
            kanaToHira(keyword),
          ),
        ),
    [users, keyword, userIds],
  );

  const selectedUsers = React.useMemo(
    () =>
      users
        .filter((user) => userIds.find((userId) => user.id === userId))
        .filter((user) => user),
    [users, userIds],
  );

  return (
    <Combobox value={userIds} onChange={onChange} multiple>
      <div
        className="relative mt-1 flex-1"
        onClick={() => inputRef.current?.focus()}>
        <div className="relative w-full min-h-[2.5rem] cursor-default rounded-sm bg-white py-2 pl-2 pr-10 flex items-center gap-1 border border-c-border focus:outline-none flex-wrap">
          {selectedUsers.map((user) => (
            <span
              key={user.id}
              className="truncate inline-flex items-center justify-center pl-2 pr-1 py-1 text-sm font-medium leading-4 rounded-sm bg-c-bg border border-c-border"
              onClick={(e) => e.stopPropagation()}>
              {user.lastName} {user.firstName}
              <XMarkIcon
                className="w-4 h-4 ml-2 cursor-pointer hover:bg-[rgba(255,255,255,.3)] rounded-sm"
                onClick={() =>
                  onChange(userIds.filter((userId) => userId !== user.id))
                }
              />
            </span>
          ))}
          <Combobox.Input
            className="flex-1 outline-none border-transparent focus:border-transparent focus:ring-0"
            onChange={(e) => setKeyword(e.target.value)}
            onFocus={() => buttonRef.current?.click()}
            value={keyword}
            ref={inputRef}
          />
          <Combobox.Button className="invisible" ref={buttonRef} />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </div>
        {filteredUsers.length > 0 && (
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredUsers.map((user) => (
                <Combobox.Option
                  key={user.id}
                  className={({active}) =>
                    `relative cursor-default select-none py-2 px-4 ${
                      active ? 'bg-c-bg' : 'white'
                    }`
                  }
                  value={user.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm bg" />
                    <span className="truncate flex-1">
                      {user.lastName} {user.firstName}
                    </span>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </Transition>
        )}
      </div>
    </Combobox>
  );
};

export default UsersSelect;
